import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useCallback } from 'react';
import { Controller, useForm, useFormState } from 'react-hook-form';

import { useAnalytics } from '~/analytics/useAnalytics';
import DialogTitleWithCloseButton from '~/components/DialogTitleWithCloseButton';
import { createFormInvalidHandler } from '~/components/form/utils';
import { supportRequestsCreate } from '~/data/generated/endpoints/support/support';
import { LiftAPIClientError } from '~/data/liftApiMutator';
import {
  type SupportRequestCreate,
  supportRequestCreateSchema,
} from '~/data/schemas/supportRequest';
import useLAISnackbar from '~/hooks/useLAISnackbar';
import { BUTTON_SX } from '~/theme';

interface SupportRequestDialogProps {
  open: boolean;
  onClose: () => void;
}

const CATEGORY_OPTIONS: { value: SupportRequestCreate['category']; label: string }[] = [
  { value: 'question', label: 'Question' },
  { value: 'problem', label: 'Problem' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'billing', label: 'Billing' },
];

const CATEGORY_LABEL_ID = 'support-request-category-label';

/**
 * Support request modal: a short form that POSTs to the stateless
 * `/api/v1/support/requests/` relay (which forwards into the Linear support
 * queue) and reports the result via snackbar.
 *
 * The relay records nothing of its own — a failed POST means the request was
 * lost. So on failure we keep the dialog open with the user's message intact
 * and surface an error; we never optimistically close-and-toast-success.
 */
export const SupportRequestDialog = ({ open, onClose }: SupportRequestDialogProps) => {
  const { showSnackbar } = useLAISnackbar();
  const { track } = useAnalytics();
  const pillSx = BUTTON_SX;

  const { control, handleSubmit, register, reset } = useForm<SupportRequestCreate>({
    mode: 'onChange',
    defaultValues: { category: 'question', message: '' },
    resolver: zodResolver(supportRequestCreateSchema),
  });
  const { isSubmitting, errors } = useFormState({ control });

  const onSubmit = useCallback(
    async (data: SupportRequestCreate) => {
      try {
        await supportRequestsCreate({
          category: data.category,
          message: data.message,
          pageUrl: window.location.href,
        });
        showSnackbar("Thanks — we'll follow up by email.", { variant: 'success' });
        track('support_request_submitted', { category: data.category });
        reset();
        onClose();
      } catch (error) {
        // The relay is stateless — a failed POST means the request was not
        // recorded anywhere. Keep the dialog open with the message intact so
        // the user can retry without retyping.
        console.error('Error submitting support request:', error);
        captureException(error);
        // Surface the relay failure to analytics so it's distinguishable from
        // user abandonment in the funnel (and alertable at launch). `status`
        // is absent for network errors that never reached the server.
        const status = error instanceof LiftAPIClientError ? error.context.status : undefined;
        track('support_request_failed', { status });
        showSnackbar(
          "Couldn't send your request — please try again, or email support@liftai.com.",
          { variant: 'error' },
        );
      }
    },
    [showSnackbar, track, reset, onClose],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: { maxHeight: '90vh' },
        },
      }}
    >
      <DialogTitleWithCloseButton onClose={onClose} title="Contact support" />
      <DialogContent>
        <Box
          component="form"
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          onSubmit={handleSubmit(
            onSubmit,
            createFormInvalidHandler<SupportRequestCreate>({
              formName: 'SupportRequestDialog',
              showSnackbar,
            }),
          )}
          noValidate
        >
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth sx={{ mt: 1 }} error={!!errors.category}>
                <InputLabel id={CATEGORY_LABEL_ID}>Category</InputLabel>
                <Select {...field} labelId={CATEGORY_LABEL_ID} label="Category">
                  {CATEGORY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <TextField
            {...register('message')}
            label="Message"
            placeholder="How can we help?"
            error={!!errors.message}
            helperText={errors.message?.message}
            multiline
            rows={4}
            fullWidth
          />

          <div
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}
          >
            <Button onClick={onClose} variant="outlined" sx={pillSx}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isSubmitting} sx={pillSx}>
              {isSubmitting ? 'Sending...' : 'Send'}
            </Button>
          </div>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
